// Timer Management System for Timed Quiz Mode

export const QUIZ_MODES = {
  PRACTICE: 'practice',
  TIMED: 'timed'
};

export const TIMER_SETTINGS = {
  easy: {
    timePerQuestion: 60, // 60 seconds for easy
    bonusPoints: 0,
    warningTime: 10 // show warning when 10 seconds left
  },
  medium: {
    timePerQuestion: 40, // 40 seconds for medium
    bonusPoints: 0,
    warningTime: 10
  },
  hard: {
    timePerQuestion: 30, // 30 seconds for hard
    bonusPoints: 0,
    warningTime: 10
  }
};

/**
 * Get timer settings based on difficulty
 */
export const getTimerSettings = (difficulty) => {
  return TIMER_SETTINGS[difficulty] || TIMER_SETTINGS.medium;
};

/**
 * Calculate time bonus based on remaining time
 * @param {number} remainingTime - Time left in seconds
 * @param {number} totalTime - Total time for question
 * @param {number} bonusPoints - Maximum bonus points
 * @returns {number} Bonus points earned
 */
export const calculateTimeBonus = (remainingTime, totalTime, bonusPoints) => {
  if (remainingTime <= 0) return 0;
  
  // Bonus is proportional to time remaining
  // Answer in first 25% of time = full bonus
  // Answer in last 25% of time = no bonus
  const percentage = remainingTime / totalTime;
  
  if (percentage >= 0.75) return bonusPoints; // Full bonus
  if (percentage >= 0.50) return Math.floor(bonusPoints * 0.75); // 75% bonus
  if (percentage >= 0.25) return Math.floor(bonusPoints * 0.50); // 50% bonus
  return Math.floor(bonusPoints * 0.25); // 25% bonus
};

/**
 * Format time for display (MM:SS)
 */
export const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Get timer color based on remaining time
 */
export const getTimerColor = (remainingTime, totalTime, warningTime) => {
  if (remainingTime <= 5) return '#e74c3c'; // Red - critical
  if (remainingTime <= warningTime) return '#f39c12'; // Orange - warning
  return '#2ecc71'; // Green - safe
};

/**
 * Get timer status
 */
export const getTimerStatus = (remainingTime, totalTime, warningTime) => {
  if (remainingTime <= 0) return 'expired';
  if (remainingTime <= 5) return 'critical';
  if (remainingTime <= warningTime) return 'warning';
  return 'safe';
};

/**
 * Calculate total quiz time
 */
export const calculateTotalQuizTime = (questionCount, difficulty) => {
  const settings = getTimerSettings(difficulty);
  return questionCount * settings.timePerQuestion;
};

/**
 * Get performance message based on time
 */
export const getTimePerformanceMessage = (averageTime, targetTime) => {
  const ratio = averageTime / targetTime;
  
  if (ratio <= 0.5) {
    return {
      title: 'Lightning Fast! ⚡',
      message: 'You answered super quickly!',
      emoji: '🚀'
    };
  } else if (ratio <= 0.75) {
    return {
      title: 'Great Speed! 🏃',
      message: 'You were quick and accurate!',
      emoji: '⭐'
    };
  } else if (ratio <= 1.0) {
    return {
      title: 'Good Timing! ⏱️',
      message: 'You used your time well!',
      emoji: '👍'
    };
  } else {
    return {
      title: 'Take Your Time! 🐢',
      message: 'Accuracy is more important than speed!',
      emoji: '💡'
    };
  }
};

/**
 * Save quiz mode preference
 */
export const saveQuizModePreference = (profileId, mode) => {
  try {
    const key = `quizMode_${profileId}`;
    localStorage.setItem(key, mode);
  } catch (error) {
    console.error('Error saving quiz mode preference:', error);
  }
};

/**
 * Load quiz mode preference
 */
export const loadQuizModePreference = (profileId) => {
  try {
    const key = `quizMode_${profileId}`;
    return localStorage.getItem(key) || QUIZ_MODES.PRACTICE;
  } catch (error) {
    console.error('Error loading quiz mode preference:', error);
    return QUIZ_MODES.PRACTICE;
  }
};

/**
 * Get mode configuration
 */
export const getModeConfig = (mode) => {
  return {
    practice: {
      id: QUIZ_MODES.PRACTICE,
      name: 'Practice Mode',
      description: 'Take your time, no pressure!',
      icon: '📚',
      color: '#3498db',
      hasTimer: false,
      hasBonus: false
    },
    timed: {
      id: QUIZ_MODES.TIMED,
      name: 'Timed Mode',
      description: 'Race against the clock!',
      icon: '⏱️',
      color: '#e74c3c',
      hasTimer: true,
      hasBonus: true
    }
  }[mode] || {
    id: QUIZ_MODES.PRACTICE,
    name: 'Practice Mode',
    description: 'Take your time, no pressure!',
    icon: '📚',
    color: '#3498db',
    hasTimer: false,
    hasBonus: false
  };
};

/**
 * Calculate quiz statistics
 */
export const calculateQuizStats = (answers, mode, difficulty) => {
  const totalQuestions = answers.length;
  const correctAnswers = answers.filter(a => a.isCorrect).length;
  const baseScore = correctAnswers;
  
  let totalBonus = 0;
  let totalTime = 0;
  let fastestTime = Infinity;
  let slowestTime = 0;
  
  if (mode === QUIZ_MODES.TIMED) {
    const settings = getTimerSettings(difficulty);
    
    answers.forEach(answer => {
      if (answer.timeSpent !== undefined) {
        totalTime += answer.timeSpent;
        fastestTime = Math.min(fastestTime, answer.timeSpent);
        slowestTime = Math.max(slowestTime, answer.timeSpent);
        
        if (answer.isCorrect && answer.timeBonus) {
          totalBonus += answer.timeBonus;
        }
      }
    });
  }
  
  return {
    totalQuestions,
    correctAnswers,
    baseScore,
    totalBonus,
    finalScore: baseScore + totalBonus,
    totalTime,
    averageTime: totalTime / totalQuestions || 0,
    fastestTime: fastestTime === Infinity ? 0 : fastestTime,
    slowestTime,
    accuracy: (correctAnswers / totalQuestions) * 100
  };
};
